import { useCallback, useMemo, useState } from "react"

import type { MetricRecord } from "@/metrics/runtime"

import { metricOutputType } from "../../utils/metricChainState"
import { ANY_RESULT, decodeTraceValue } from "../recentMetricValues"

export function useRecentMetricFilter(metric: MetricRecord | null) {
  const outputType = metricOutputType(metric) ?? "string"
  const isFloatMetric = outputType === "float"
  const isValuesMetric =
    metric?.metric_manager === "manager_custom_trace" && outputType === "set"
  const isTraceMetric = metric?.metric_manager === "manager_custom_trace"
  const filterKey = `${metric?.id ?? ""}:${outputType}`
  const [filterState, setFilterState] = useState({ key: filterKey, value: "" })
  const filterValue = filterState.key === filterKey ? filterState.value : ""
  const setFilterValue = useCallback(
    (value: string) => setFilterState({ key: filterKey, value }),
    [filterKey],
  )
  const [filterOperator, setFilterOperator] = useState<
    ">" | "<" | "=" | ">=" | "<="
  >("=")
  const isAnyResult = filterValue === ANY_RESULT
  const activeMetricValueFilters = useMemo(() => {
    if (!filterValue || !metric?.id || isAnyResult) {
      return undefined
    }
    const traceValue = isTraceMetric ? decodeTraceValue(filterValue) : null
    const value = isValuesMetric ? traceValue : (traceValue ?? filterValue)
    if (value === null) {
      return undefined
    }
    return [
      {
        metricId: metric.id,
        operator:
          isFloatMetric && traceValue === null
            ? filterOperator
            : ("=" as const),
        value,
        valueType:
          isFloatMetric && traceValue === null
            ? ("float" as const)
            : ("string" as const),
      },
    ]
  }, [
    metric?.id,
    filterValue,
    filterOperator,
    isFloatMetric,
    isValuesMetric,
    isTraceMetric,
    isAnyResult,
  ])
  return {
    isFloatMetric,
    isValuesMetric,
    isTraceMetric,
    filterValue,
    setFilterValue,
    filterOperator,
    setFilterOperator,
    isAnyResult,
    activeMetricValueFilters,
  }
}
